import { useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { School } from '../../../types/admin';
import { useNavigate } from 'react-router-dom';
import { useSchool } from '../../../contexts/SchoolContext';
import toast from 'react-hot-toast';

export function useSchoolActions() {
  const [loading, setLoading] = useState(false);
  const { setSelectedSchool } = useSchool();
  const navigate = useNavigate();

  const generateSchoolCode = (name: string) => {
    const prefix = name.substring(0, 3).toUpperCase();
    const year = new Date().getFullYear();
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${prefix}${year}${random}`;
  };

  const handleJoinSchool = async (schoolCode: string) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      console.log('[SchoolSelector] Joining school with code:', schoolCode);

      const schoolQuery = query(
        collection(db, 'schools'),
        where('code', '==', schoolCode.trim())
      );

      const querySnapshot = await getDocs(schoolQuery);
      if (querySnapshot.empty) {
        toast.error('Invalid school code');
        return;
      }

      const schoolDoc = querySnapshot.docs[0];
      const school = { id: schoolDoc.id, ...schoolDoc.data() } as School;

      const membershipId = `${school.id}_${user.uid}`;
      await setDoc(doc(db, 'school_members', membershipId), {
        userId: user.uid,
        schoolId: school.id,
        role: 'staff',
        joinedAt: new Date().toISOString()
      });

      await setSelectedSchool(school);
      toast.success('Successfully joined school');
      navigate('/dashboard');
    } catch (error) {
      console.error('[SchoolSelector] Error joining school:', error);
      toast.error('Failed to join school');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchool = async (schoolData: Partial<School>) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      // Create a new document reference with auto-generated ID
      const schoolRef = doc(collection(db, 'schools'));
      const code = generateSchoolCode(schoolData.name || '');

      // Create the school object with the generated ID
      const newSchool: School = {
        id: schoolRef.id, // Use the generated ID
        name: schoolData.name?.trim() || '',
        code,
        adminId: user.uid,
        type: schoolData.type || 'public',
        address: schoolData.address?.trim(),
        city: schoolData.city?.trim(),
        state: schoolData.state?.trim(),
        zipCode: schoolData.zipCode?.trim(),
        phone: schoolData.phone?.trim(),
        website: schoolData.website?.trim(),
        gradeRange: schoolData.gradeRange || { start: 'K', end: '12' },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        settings: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          academicYear: new Date().getFullYear().toString(),
          terms: [],
          departments: []
        },
        stats: {
          totalStaff: 1,
          totalStudents: 0,
          activeUsers: 1
        }
      };

      // Save the school document
      await setDoc(schoolRef, newSchool);

      // Create school membership for the admin
      const membershipId = `${newSchool.id}_${user.uid}`;
      await setDoc(doc(db, 'school_members', membershipId), {
        userId: user.uid,
        schoolId: newSchool.id,
        role: 'admin',
        joinedAt: new Date().toISOString()
      });

      // Update selected school in context
      await setSelectedSchool(newSchool);
      toast.success('School created successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('[SchoolSelector] Error creating school:', error);
      toast.error('Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSchool = async (school: School) => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;

      const membershipQuery = query(
        collection(db, 'school_members'),
        where('userId', '==', user.uid),
        where('schoolId', '==', school.id)
      );
      const membershipSnapshot = await getDocs(membershipQuery);

      if (membershipSnapshot.empty) {
        throw new Error('No valid membership found');
      }

      await setSelectedSchool(school);
      toast.success(`Selected ${school.name}`);
      navigate('/dashboard');
    } catch (error) {
      console.error('[SchoolSelector] Error selecting school:', error);
      toast.error('Failed to select school');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleJoinSchool,
    handleCreateSchool,
    handleSelectSchool
  };
}